// CUBBO-2461 — Partial fulfillment ("tarja") error classification.
//
// The "Enviar parcial" flow submits a batch and polls the async-task tracker
// (see services/orderServices.js). A failure can surface either as the Error
// thrown by fetchPartialFulfillmentResult (err.data = { failures, failInfo })
// or as a raw HTTP error envelope (err.response.data...).
//
// Four failure reasons need a distinct, actionable message instead of the raw backend text:
// an approved fiscal invoice (NF-e) that must be regenerated before the tarja (platform guard
// precondition_no_approved_nfe), an order whose pieces were written off to LOST, which the
// tarja cannot repair at all, a partial shipment that would split a container, and an order
// holding container stock that the classic in-review tarja cannot handle.
//
// BACKEND CONTRACT (platform CUBBO-2461 Task 7): the fiscal block arrives as the
// stable code `blocked_by_approved_nfe` (AdjustLineQuantitiesError). Because the
// exact carrier field can vary (Sidekiq Pro batch fail_info only preserves
// `error_class` + `message`), detection here is defensive: it scans code /
// error_code / error_class / message across both the batch failInfo and the HTTP
// envelope. The retired `nfe_absent` code (the pre-Task-7 name of the same guard)
// is intentionally NOT matched — the backend no longer emits it, and as a
// free-text substring it risked false positives (review J5 / Francisco).

// Canonical code the UI keys on for a fiscal (approved NF-e) block.
export const PARTIAL_FULFILL_ERROR_FISCAL_BLOCK = 'blocked_by_approved_nfe'

// The order lost pieces to LOST from review, so its line still asks for units that are no
// longer attached to it. The tarja cannot fix that — its engine only subtracts units still
// hanging off the line — so the only way out is Revert Picking.
export const PARTIAL_FULFILL_ERROR_MISSING_STOCK = 'ORDER_MISSING_STOCK'

// Shipping partially would leave a container half-emptied, which nobody can do on the floor.
// Revert Picking is the way out: it takes the containers back with the whole order.
export const PARTIAL_FULFILL_ERROR_WOULD_CUT_CONTAINER = 'PARTIAL_FULFILLMENT_WOULD_CUT_CONTAINER'

// The classic in-review tarja cannot handle stock held inside boxes or pallets, so it refuses the
// whole order. Revert Picking is the way out, as above.
export const PARTIAL_FULFILL_ERROR_NOT_SUPPORTED_FOR_CONTAINERS = 'PARTIAL_FULFILLMENT_NOT_SUPPORTED_FOR_CONTAINERS'

// Refusals that get their own view in the dialog instead of the raw backend text.
export const PARTIAL_FULFILL_BLOCKING_CODES = [
  PARTIAL_FULFILL_ERROR_FISCAL_BLOCK,
  PARTIAL_FULFILL_ERROR_MISSING_STOCK,
  PARTIAL_FULFILL_ERROR_WOULD_CUT_CONTAINER,
  PARTIAL_FULFILL_ERROR_NOT_SUPPORTED_FOR_CONTAINERS,
]

// Canonical code → substrings (case-insensitive) that identify it. Each is specific enough
// to be safe as a substring match.
const ERROR_SIGNALS = [
  [PARTIAL_FULFILL_ERROR_FISCAL_BLOCK, ['blocked_by_approved_nfe']],
  [PARTIAL_FULFILL_ERROR_MISSING_STOCK, ['order_missing_stock']],
  [PARTIAL_FULFILL_ERROR_WOULD_CUT_CONTAINER, ['partial_fulfillment_would_cut_container']],
  [PARTIAL_FULFILL_ERROR_NOT_SUPPORTED_FOR_CONTAINERS, ['partial_fulfillment_not_supported_for_containers']],
]

const normalize = value => (typeof value === 'string' ? value.trim().toLowerCase() : '')

// Returns the canonical code this value carries, or null.
const matchCode = value => {
  const normalized = normalize(value)
  if (!normalized) return null
  const hit = ERROR_SIGNALS.find(([, signals]) => signals.some(signal => normalized.includes(signal)))
  return hit ? hit[0] : null
}

// Collect every failure entry we might have, from either the batch-tracker path
// (error.data.failInfo, thrown by fetchPartialFulfillmentResult) or an HTTP
// error envelope (error.response.data.failInfo / fail_info).
// `errors` is the shape the CLASSIC tarja endpoint rejects with — it accepts several picking
// orders, so it answers with an array of { message, code, context }. Nothing read it before,
// which left every refusal from that endpoint falling through to axios' own message.
const collectFailInfo = error => {
  const buckets = [
    error?.data?.failInfo,
    error?.response?.data?.failInfo,
    error?.response?.data?.fail_info,
    error?.response?.data?.errors,
  ]
  return buckets.filter(Array.isArray).flat()
}

const failInfoCode = entry =>
  matchCode(entry?.code) || matchCode(entry?.error_code) || matchCode(entry?.error_class) || matchCode(entry?.message)

/**
 * Inspect a caught partial-fulfillment error and return the canonical UI error code it carries.
 *
 * @param {unknown} error - the error caught in onConfirmPartialFulfill.
 * @returns {string|null} one of PARTIAL_FULFILL_BLOCKING_CODES, or null when it is none of them.
 */
export const getPartialFulfillErrorCode = error => {
  if (!error) return null

  for (const entry of collectFailInfo(error)) {
    const code = failInfoCode(entry)
    if (code) return code
  }

  const envelope = error?.response?.data
  const nested = envelope?.error
  return (
    matchCode(envelope?.code) ||
    matchCode(envelope?.error) ||
    matchCode(nested?.code) ||
    matchCode(nested?.error_code) ||
    matchCode(nested?.error_class) ||
    matchCode(nested?.message) ||
    matchCode(error?.message)
  )
}

/**
 * Resolve an always-STRING message from a caught partial-fulfillment error, for the
 * generic error view. Picking-order endpoints can return { error: { code, message } };
 * storing that object and rendering it raw crashes React ("Objects are not valid as
 * a React child"). J4.
 *
 * @param {unknown} error - the error caught in onConfirmPartialFulfill.
 * @returns {string} a display string ('' when nothing usable).
 */
export const getPartialFulfillErrorMessage = error => {
  const envelopeError = error?.response?.data?.error
  const firstListed = collectFailInfo(error).map(entry => entry?.message).find(message => typeof message === 'string')
  const candidate =
    (envelopeError && typeof envelopeError === 'object' ? envelopeError.message : envelopeError) ||
    firstListed ||
    error?.message
  return typeof candidate === 'string' ? candidate : ''
}
